import type { CorrectAnswer } from "@labeling-tools/api/field-rep/generated/types.gen";
import { ProductProposal } from "@/types/trackLabeling";

/** What the labeler submitted: a product, or one of the two Skips. */
export type LabelerAnswer =
  | { kind: "product"; product: ProductProposal }
  | { kind: "productNotListed" }
  | { kind: "badDetection" };

export type CorrectAnswerMessage = {
  /** Where to look in the track, shown before the correct answer. */
  cue: string;
  /** Why the labeler's answer was incorrect, shown with the correct answer. */
  body: string;
  /** The field that differentiates the labeler's product from the correct product. */
  differingField: "brand" | "cluster" | "variant" | null;
};

/**
 * The catalog fields that differentiate two products apart, carried by both the
 * picked and the correct product.
 */
type ComparableProductFields = Pick<ProductProposal, "cluster" | "variant">;

/**
 * Describes the first field that differentiates the two products. Null when the we can't tell
 * the products apart, e.g. a product has no cluster or variant.
 */
const describeProductDifference = (
  picked: ComparableProductFields,
  correct: ComparableProductFields,
): CorrectAnswerMessage | null => {
  if (!picked.cluster || !correct.cluster) {
    return null;
  }

  if (picked.cluster.brand.id !== correct.cluster.brand.id) {
    return {
      cue: "Look closely at the brand on the packaging.",
      body:
        `You picked ${picked.cluster.brand.name}. ` +
        `The correct one is ${correct.cluster.brand.name}. ` +
        "Check the brand name on the packaging.",
      differingField: "brand",
    };
  }

  if (picked.cluster.id !== correct.cluster.id) {
    return {
      cue: "Look closely at the product name on the packaging.",
      body:
        // Include the brand: a cluster name alone, e.g. "Original", doesn't say which product it is.
        `You picked ${picked.cluster.brand.name} ${picked.cluster.name}. ` +
        `The correct one is ${correct.cluster.brand.name} ${correct.cluster.name}. ` +
        "Check the product name on the packaging.",
      differingField: "cluster",
    };
  }

  const pickedVariant = picked.variant?.trim();
  const correctVariant = correct.variant?.trim();
  if (pickedVariant && correctVariant && pickedVariant !== correctVariant) {
    return {
      cue: "Pay attention to the size and pack size.",
      body:
        `You picked ${pickedVariant}. ` +
        `The correct one is ${correctVariant}. ` +
        "Check the size and pack size.",
      differingField: "variant",
    };
  }

  return null;
};

/**
 * The message for a correct answer, from what the labeler submitted. The correct answer is
 * returned from the backend only when the labeler's answer was incorrect.
 */
export const describeCorrectAnswer = (
  labelerAnswer: LabelerAnswer,
  correctAnswer: CorrectAnswer,
): CorrectAnswerMessage => {
  switch (labelerAnswer.kind) {
    case "product": {
      // Labeler should skip the track.
      if (!correctAnswer.product) {
        return {
          cue: "Look closely: does the highlighted product really match your answer?",
          body:
            "None of the products match. " +
            "Skip instead of picking the closest one.",
          differingField: null,
        };
      }
      // Labeler picked the wrong product.
      return (
        describeProductDifference(
          labelerAnswer.product,
          correctAnswer.product,
        ) ?? {
          cue: "Look closely at the highlighted product.",
          body: "Compare the two products with the track.",
          differingField: null,
        }
      );
    }
    // Labeler skipped the task at track stage.
    case "badDetection":
      return {
        cue: "Follow the highlighted product from the first frame to the last.",
        body: "Skip the track only when the highlight moves to another product.",
        differingField: null,
      };
    // Labeler skipped the task at product picking stage.
    case "productNotListed":
      return {
        cue: "Look closely at the highlighted product.",
        body: "A product matches this track. Search for it before you skip.",
        differingField: null,
      };
    default: {
      const unhandledAnswer: never = labelerAnswer;
      return unhandledAnswer;
    }
  }
};
